import * as React from "react";
import { useState, useEffect } from "react";
import { Loader, Message, Input, Button, Modal } from "semantic-ui-react";
import { AdminServices } from "@/services/bedrock";

interface Props {
  open: boolean;
  onClose: () => void;
  targetEmail: string; // The email of the user getting the reset link
}

type Step = "request" | "verify" | "success";

const RequestPWModalFC: React.FC<Props> = ({ open, targetEmail, onClose }) => {
  const [step, setStep] = useState<Step>("request");
  const [token, setToken] = useState<string>("");
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep("request");
      setToken("");
      setGeneratedLink("");
      setError(null);
    }
  }, [open]);

  const handleRequestCode = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await AdminServices.registerPasswordLinkIntent(targetEmail);
      setStep("verify");
    } catch (err: any) {
      console.log(err);
      setError(err?.response?.data?.error || "Failed to send verification code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await AdminServices.verifyPasswordLinkIntent(targetEmail, token.trim());
      if (response?.link) {
        setGeneratedLink(`${window.location.origin}${response.link}`);
        setStep("success");
      }
    } catch (err: any) {
      console.log(err);
      setError(err?.response?.data?.error || "Invalid or expired token.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyAndClose = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
    } catch (err) {
      console.error("Failed to copy link", err);
    }
    onClose();
  };

  const getModalConfig = () => {
    switch (step) {
      case "request":
        return {
          title: "Generate Password Link",
          acceptLabel: "Send Code",
          acceptHandler: handleRequestCode,
          disableAccept: isLoading,
          showCancel: true,
        };
      case "verify":
        return {
          title: "Verify Identity",
          acceptLabel: "Verify & Generate",
          acceptHandler: handleVerifyCode,
          disableAccept: isLoading || token.length < 6,
          showCancel: true,
        };
      case "success":
        return {
          title: "Link Generated Successfully",
          acceptLabel: "Done",
          acceptHandler: onClose,
          disableAccept: false,
          showCancel: false,
        };
    }
  };

  const { title, acceptLabel, acceptHandler, disableAccept, showCancel } = getModalConfig();

  return (
    <Modal open={open} onClose={onClose} size="tiny" dimmer="blurring">
      <Modal.Header>{title}</Modal.Header>

      <Modal.Content>
        {isLoading && <Loader active inline="centered" />}

        {!isLoading && error && (
          <Message negative>
            <p>{error}</p>
          </Message>
        )}

        {!isLoading && step === "request" && (
          <div>
            <p>
              You are about to generate a secure password reset link for <b>{targetEmail}</b>.
            </p>
            <p>Click below to send a 6-character verification code.</p>
          </div>
        )}

        {!isLoading && step === "verify" && (
          <div>
            <p>
              A verification code was sent to you. Enter it below to authorize the link generation for{" "}
              <b>{targetEmail}</b>.
            </p>
            <label>
              <b>Verification Code</b>
            </label>
            <Input
              fluid
              placeholder="e.g. A1B2C3"
              value={token}
              onChange={e => setToken(e.target.value.toUpperCase())}
              maxLength={6}
              autoFocus
            />
            <Message size="small" color="yellow">
              <Message.Header>Time Sensitive</Message.Header>
              <p>
                Your verification code will expire in <b>10 minutes</b>.
              </p>
            </Message>
          </div>
        )}

        {!isLoading && step === "success" && (
          <div>
            <Message info>
              <Message.Header>Security Best Practices</Message.Header>
              <Message.List>
                <Message.Item>
                  <b>Verify Identity:</b> Ensure you are actively communicating with the verified account owner before
                  sharing this link.
                </Message.Item>
                <Message.Item>
                  <b>Share, Don't Set:</b> It is strictly safer to provide this link to the user directly rather than
                  opening it yourself to set a password for them.
                </Message.Item>
              </Message.List>
            </Message>

            <p>
              The password reset link for <b>{targetEmail}</b> is ready and will expire in <b>24 hours</b>.
            </p>

            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "15px" }}>
              <div
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "4px",
                  border: "1px solid #d1d5db",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                }}
              >
                <a
                  href={generatedLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontWeight: "500", color: "#2185d0", textDecoration: "none" }}
                >
                  {generatedLink}
                </a>
              </div>
              <Button color="teal" icon="copy" content="Copy" onClick={handleCopyAndClose} />
            </div>
          </div>
        )}
      </Modal.Content>

      <Modal.Actions>
        {showCancel && (
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button color="teal" onClick={acceptHandler} disabled={disableAccept} loading={isLoading}>
          {acceptLabel}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const RequestPWModal = RequestPWModalFC;
